import { GET, MPUT, POST } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import OneSignal from 'react-onesignal';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { NOTIFICATION_TYPES } from 'types/Notification.ts';
import type { UserSettings } from 'types/UserSettings.ts';
import { IconBadge } from 'ui/component/IconBadge.tsx';
import { IBell, IBellSilent } from 'ui/component/Icons.tsx';

type Props = {
  className?: string;
};

export const NotificationBell = ({ className }: Props) => {
  const [consentStatus, setConsentStatus] = React.useState<null | 'default' | 'granted' | 'denied'>(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const { data: rowsCount } = useSWR(['/notification/count', { read_at: null }], POST);
  const { data: userSettings, isLoading } = useSWR<UserSettings>(['/user-settings'], GET);
  const setUserSettingsMutation = useSWRMutation('/user-settings', MPUT);

  React.useEffect(() => {
    (async () => {
      // @ts-expect-error OneSignal is not typed/exported correctly
      i18n.on('languageChanged', (lng) => OneSignal.User.setLanguage(lng));
      setConsentStatus(Notification.permission);
    })();
  }, [i18n]);

  const onBellClick = React.useCallback(async () => {
    if (consentStatus === 'default') {
      // @ts-expect-error OneSignal is not typed/exported correctly
      await OneSignal.Notifications.requestPermission();
      // If user has not set their notification settings, set them to all notifications
      if (!isLoading && userSettings && userSettings.subscribed_types === null && userSettings.shipments_silenced === null) {
        await setUserSettingsMutation.trigger({
          subscribed_types: Object.values(NOTIFICATION_TYPES),
          shipments_silenced: [],
        });
      }
      setConsentStatus(Notification.permission);
    } else if (consentStatus === 'denied') {
      // @ts-expect-error OneSignal is not typed/exported correctly
      await OneSignal.Notifications.requestPermission();
      setConsentStatus(Notification.permission);
    } else if (consentStatus === 'granted') {
      navigate('/notifications');
    }
  }, [consentStatus, navigate, isLoading, userSettings, setUserSettingsMutation]);

  const unreadBadge = React.useMemo(() => {
    if (!rowsCount) return null;
    return rowsCount <= 9 ? String(rowsCount) : '∞';
  }, [rowsCount]);

  return (
    <div className={className}>
      {consentStatus === null && <IBell size={20} className="ml-2 fill-white opacity-70 hover:opacity-100" />}
      {consentStatus === 'default' && (
        <IconBadge icon={<IBell size={20} className="ml-2 fill-white opacity-70 hover:opacity-100" />} badge={'!'} color={'red'} onClick={onBellClick} />
      )}
      {consentStatus === 'denied' && <IconBadge icon={<IBellSilent size={20} className="ml-2 fill-white opacity-70 hover:opacity-100" />} onClick={onBellClick} badge={''} />}
      {consentStatus === 'granted' && (
        <IconBadge icon={<IBell size={20} className="ml-2 fill-white opacity-70 hover:opacity-100" />} badge={unreadBadge} color={'red'} onClick={onBellClick} />
      )}
    </div>
  );
};
